import { ReactNode, useState } from "react"
import { DateTime } from "luxon"
import { Box, Collapse, IconButton, LinearProgress, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleLeft, faChevronDown, faChevronUp, faTruck } from "@fortawesome/pro-light-svg-icons"
import { faPen } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useDateTime } from "../../../../../../localization/useDateTime"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { MatchEnvironment } from "../../../../../../environment/components/MatchEnvironment"
import { EnvironmentType } from "../../../../../../environment/envTypes"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { StandardLoadingButton } from "../../../../../../../shared/components/buttons/LoadingButton"
import { StandardLightBlueButton } from "../../../../../../../shared/components/buttons/StandardLightBlueButton"
import { SeatsBadge } from "../../../../../../../shared/components/seatsBadge/SeatsBadge"
import { DeliveryType, MeetingServiceOrder } from "../../meetingServiceTypes"
import { ProductionOrderComment } from "./ProductionOrderComment"
import { ProductionOrderLineRow } from "./ProductionOrderLineRow"
import {
    ProductionDeliveryGroup,
    ProductionEditLine,
    ProductionProductOption,
    SetLineProductInput,
    SetLineWishInput,
} from "../useProductionOrderEdit"

const useStyles = makeStyles()((theme) => ({
    deliveryHeaderWrapper: {
        position: "relative",
    },
    // Background-refresh indicator on the delivery being saved (an order can have several deliveries).
    savingBar: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1,
    },
    deliveryHeader: {
        padding: ".75rem 1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        [theme.breakpoints.down("md")]: {
            flexWrap: "wrap",
        },
    },
    headerLeft: {
        flexWrap: "wrap",
        minWidth: 0,
    },
    chevron: {
        fontSize: "1rem",
        color: theme.palette.tailwind.slate[500],
    },
    chevronBox: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxSizing: "border-box",
        width: "1.875rem",
        height: "1.875rem",
        padding: 0,
        color: theme.palette.tailwind.slate[500],
    },
    timeBlock: {
        flexShrink: 0,
        width: "6.2rem",
        [theme.breakpoints.down("md")]: {
            width: "5.5rem",
        },
    },
    bigTime: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[950],
        whiteSpace: "nowrap",
        [theme.breakpoints.down("md")]: {
            fontSize: "1.125rem",
        },
    },
    bar: {
        flexShrink: 0,
        width: ".18rem",
        height: "2.5rem",
        background: theme.palette.tailwind.slate[200],
    },
    headerActions: {
        [theme.breakpoints.down("md")]: {
            marginLeft: "auto",
        },
    },
    editActionButton: {
        flexShrink: 0,
        "&.MuiButton-root": {
            height: "3rem",
            borderRadius: ".75rem",
            fontSize: ".875rem",
            fontWeight: 500,
        },
    },
    cancelButton: {
        "&.MuiButton-root": {
            padding: ".75rem 1.25rem",
        },
    },
    itemsBox: {
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".625rem",
        background: theme.palette.background.paper,
        overflow: "hidden",
        "& > *:last-child": {
            borderBottom: "none",
        },
    },
    itemsArea: {
        padding: "1rem 1rem 1rem 8.4rem",
        background: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        [theme.breakpoints.down("lg")]: {
            paddingLeft: "1rem",
        },
    },
    commentArea: {
        padding: "0 1rem 1rem 8.4rem",
        background: theme.palette.background.paper,
        [theme.breakpoints.down("lg")]: {
            paddingLeft: "1rem",
        },
    },
    itemsAreaLast: {
        borderBottom: "none",
    },
    editItems: {
        padding: "1rem 1rem 0 8.4rem",
        background: theme.palette.background.paper,
        [theme.breakpoints.down("lg")]: {
            paddingLeft: "1rem",
        },
    },
}))

const useDeliveryLabelStyles = makeStyles()((theme) => ({
    root: {
        display: "inline-flex",
        alignItems: "center",
        gap: "1.5rem",
    },
    delivery: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".5rem",
    },
    deliveryIcon: {
        fontSize: "1rem",
        color: theme.palette.tailwind.slate[400],
    },
    deliveryText: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[800],
        whiteSpace: "nowrap",
    },
}))

function DeliveryLabel({ order }: Readonly<{ order: MeetingServiceOrder }>) {
    const { classes } = useDeliveryLabelStyles()
    const { l10n } = useAppLocalization()

    const { deliveryDetails } = order
    const deliveryTypeLabel = l10n.getStringForEnum("delivery-type-filter", deliveryDetails.type, deliveryDetails.type)
    const isCanteenOrPickup =
        deliveryDetails.type === DeliveryType.EATING_IN_CANTEEN || deliveryDetails.type === DeliveryType.PICK_UP
    const deliveryLocationName = !isCanteenOrPickup ? deliveryDetails.location?.name : undefined
    // Canteen/pickup orders have no delivery location, but a location-required extra service can still set a
    // meeting location — show it next to the fulfilment type when present.
    const meetingLocationSuffix =
        isCanteenOrPickup && deliveryDetails.location?.name ? ` · ${deliveryDetails.location.name}` : ""

    return (
        <Box className={classes.root}>
            <Box className={classes.delivery}>
                <FontAwesomeIcon icon={faTruck} className={classes.deliveryIcon} />
                {deliveryLocationName ? (
                    <LocalizedStrict id="meeting-service-table-meeting" vars={{ locationName: deliveryLocationName }}>
                        <Typography variant="body1" component="span" className={classes.deliveryText}>
                            {`Delivery: ${deliveryLocationName}`}
                        </Typography>
                    </LocalizedStrict>
                ) : (
                    <Typography variant="body1" component="span" className={classes.deliveryText}>
                        {`${deliveryTypeLabel}${meetingLocationSuffix}`}
                    </Typography>
                )}
            </Box>

            {!!deliveryDetails.sittings && (
                <SeatsBadge
                    count={deliveryDetails.sittings}
                    tooltipTitleId="meeting-service-production-view-sittings-label"
                />
            )}
        </Box>
    )
}

type MergedDeliveryGroup = Readonly<{
    deliveryKey: string
    deliveryTime: DateTime
    lines: ProductionEditLine[]
    sourceKeys: string[]
}>

function mergeGroupsByTime(groups: ReadonlyArray<ProductionDeliveryGroup>): MergedDeliveryGroup[] {
    const merged = new Map<
        string,
        { deliveryKey: string; deliveryTime: DateTime; lines: ProductionEditLine[]; sourceKeys: string[] }
    >()

    for (const group of groups) {
        const timeKey = group.deliveryTime.isValid ? (group.deliveryTime.toISO() ?? "invalid") : "invalid"
        const existing = merged.get(timeKey)
        if (existing) {
            existing.lines.push(...group.lines)
            existing.sourceKeys.push(group.deliveryKey)
        } else {
            merged.set(timeKey, {
                deliveryKey: timeKey,
                deliveryTime: group.deliveryTime,
                lines: [...group.lines],
                sourceKeys: [group.deliveryKey],
            })
        }
    }

    // Extra services are kept together at the bottom of each delivery, never mixed with product lines.
    return Array.from(merged.values(), (group) => ({
        ...group,
        lines: [
            ...group.lines.filter((line) => !line.isExtraService),
            ...group.lines.filter((line) => line.isExtraService),
        ],
    }))
}

type Props = Readonly<{
    order: MeetingServiceOrder
    groups: ReadonlyArray<ProductionDeliveryGroup>
    editingKey: string | null
    isSaving: boolean
    hasIncompleteLine: boolean
    hasOnlyExtraServices: boolean
    saveAttempted: boolean
    touchedKeys: ReadonlyArray<string>
    usedProductIds: ReadonlyArray<number>
    onCountChange: (key: string, value: number) => void
    onRemove: (key: string) => void
    onStartEdit: (deliveryKey: string, deliveryTime: DateTime) => void
    onSave: () => void
    onCancel: () => void
    productOptions: ReadonlyArray<ProductionProductOption>
    isProductsLoading: boolean
    isEditLoading: boolean
    onProductChange: (key: string, product: SetLineProductInput) => void
    onWishChange: (key: string, wish: SetLineWishInput) => void
    editFooter: ReactNode
}>

export function ProductionOrderLines({
    order,
    groups,
    editingKey,
    isSaving,
    hasIncompleteLine,
    hasOnlyExtraServices,
    saveAttempted,
    touchedKeys,
    usedProductIds,
    onCountChange,
    onRemove,
    onStartEdit,
    onSave,
    onCancel,
    productOptions,
    isProductsLoading,
    isEditLoading,
    onProductChange,
    onWishChange,
    editFooter,
}: Props) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set())

    const toggleGroup = (deliveryKey: string) =>
        setCollapsedKeys((prev) => {
            const next = new Set(prev)
            if (next.has(deliveryKey)) {
                next.delete(deliveryKey)
            } else {
                next.add(deliveryKey)
            }
            return next
        })

    const displayGroups = mergeGroupsByTime(groups)

    return (
        <Stack>
            {displayGroups.map((group, index) => {
                const isValidTime = group.deliveryTime.isValid
                // The rail next to a delivery always shows the delivery time — a single time of day,
                // never the meeting period (that one lives in the card header).
                const time = isValidTime ? dateTimeFactory.toHourMinute(group.deliveryTime) : "-"
                const groupEditing = editingKey === group.deliveryKey
                const isGroupLoading = groupEditing && (isProductsLoading || isSaving)

                if (groupEditing) {
                    return (
                        <Box key={group.deliveryKey}>
                            <Box className={classes.deliveryHeaderWrapper}>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    spacing="1rem"
                                    className={classes.deliveryHeader}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing="1rem"
                                        className={classes.headerLeft}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing=".5rem"
                                            className={classes.timeBlock}
                                        >
                                            <Box component="span" className={classes.chevronBox}>
                                                <FontAwesomeIcon icon={faChevronUp} className={classes.chevron} />
                                            </Box>
                                            <Typography variant="h5" component="span" className={classes.bigTime}>
                                                {time}
                                            </Typography>
                                        </Stack>
                                        <Box className={classes.bar} />
                                        <DeliveryLabel order={order} />
                                    </Stack>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing=".75rem"
                                        className={classes.headerActions}
                                    >
                                        <LocalizedStrict id="meeting-service-production-view-cancel-button">
                                            <StandardButton
                                                fullWidth={false}
                                                type="button"
                                                size="small"
                                                variant="outlined"
                                                color="grey"
                                                className={cx(classes.editActionButton, classes.cancelButton)}
                                                startIcon={<FontAwesomeIcon icon={faAngleLeft} />}
                                                onClick={onCancel}
                                            >
                                                Cancel
                                            </StandardButton>
                                        </LocalizedStrict>
                                        <LocalizedStrict id="meeting-service-production-view-save-changes-button">
                                            <StandardLoadingButton
                                                fullWidth={false}
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                className={classes.editActionButton}
                                                startIcon={<FontAwesomeIcon icon={faPen} />}
                                                loading={isSaving}
                                                disabled={saveAttempted && (hasIncompleteLine || hasOnlyExtraServices)}
                                                onClick={onSave}
                                            >
                                                Done
                                            </StandardLoadingButton>
                                        </LocalizedStrict>
                                    </Stack>
                                </Stack>
                                {isGroupLoading && <LinearProgress className={classes.savingBar} />}
                            </Box>
                            <Stack spacing=".75rem" className={classes.editItems}>
                                {group.lines.map((line) => (
                                    <ProductionOrderLineRow
                                        key={line.key}
                                        line={line}
                                        isEditing={groupEditing}
                                        productOptions={productOptions}
                                        isProductsLoading={isProductsLoading}
                                        isEditLoading={isEditLoading}
                                        isTouched={touchedKeys.includes(line.key)}
                                        usedProductIds={usedProductIds}
                                        onCountChange={onCountChange}
                                        onProductChange={onProductChange}
                                        onWishChange={onWishChange}
                                        onRemove={onRemove}
                                    />
                                ))}
                            </Stack>
                            {editFooter}
                            {order.note && (
                                <Box className={classes.commentArea}>
                                    <ProductionOrderComment note={order.note} />
                                </Box>
                            )}
                        </Box>
                    )
                }

                const isOpen = !collapsedKeys.has(group.deliveryKey)
                const isLastGroup = index === displayGroups.length - 1

                return (
                    <Box key={group.deliveryKey}>
                        <Box className={classes.deliveryHeaderWrapper}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                spacing="1rem"
                                className={classes.deliveryHeader}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing="1rem"
                                    className={classes.headerLeft}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing=".5rem"
                                        className={classes.timeBlock}
                                    >
                                        <IconButton
                                            size="small"
                                            className={classes.chevronBox}
                                            aria-label={l10n.getString(
                                                isOpen ? "user-home-meetings-collapse" : "user-home-meetings-expand",
                                            )}
                                            onClick={() => toggleGroup(group.deliveryKey)}
                                        >
                                            <FontAwesomeIcon
                                                icon={isOpen ? faChevronUp : faChevronDown}
                                                className={classes.chevron}
                                            />
                                        </IconButton>
                                        <Typography variant="h5" component="span" className={classes.bigTime}>
                                            {time}
                                        </Typography>
                                    </Stack>
                                    <Box className={classes.bar} />
                                    <DeliveryLabel order={order} />
                                </Stack>
                                {/* Quick (inline) edit — gated to non-prod while the edit API is being tested. */}
                                <MatchEnvironment
                                    environments={[
                                        EnvironmentType.Local,
                                        EnvironmentType.Test,
                                        EnvironmentType.Demo,
                                        EnvironmentType.QA,
                                    ]}
                                >
                                    <LocalizedStrict id="meeting-service-production-view-edit-items-button">
                                        <StandardLightBlueButton
                                            className={classes.headerActions}
                                            startIcon={<FontAwesomeIcon icon={faPen} />}
                                            onClick={() => onStartEdit(group.deliveryKey, group.deliveryTime)}
                                        >
                                            Edit
                                        </StandardLightBlueButton>
                                    </LocalizedStrict>
                                </MatchEnvironment>
                            </Stack>
                        </Box>

                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Stack
                                spacing="1rem"
                                className={cx(classes.itemsArea, isLastGroup && classes.itemsAreaLast)}
                            >
                                <Box className={classes.itemsBox}>
                                    {group.lines.map((line) => (
                                        <ProductionOrderLineRow
                                            key={line.key}
                                            line={line}
                                            isEditing={groupEditing}
                                            productOptions={productOptions}
                                            isProductsLoading={isProductsLoading}
                                            isEditLoading={isEditLoading}
                                            isTouched={touchedKeys.includes(line.key)}
                                            usedProductIds={usedProductIds}
                                            onCountChange={onCountChange}
                                            onProductChange={onProductChange}
                                            onWishChange={onWishChange}
                                            onRemove={onRemove}
                                        />
                                    ))}
                                </Box>
                                {order.note && <ProductionOrderComment note={order.note} />}
                            </Stack>
                        </Collapse>
                    </Box>
                )
            })}
        </Stack>
    )
}
